import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, UncontrolledTooltip, Spinner } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { getAllOrganizations, toggleOrganizationBrandSetupFlow } from '../ReduxToolkit/Reducers/organizationManagement';

function InviteSuccessModal({
  open,
  organizationName,
  onClose,
  onViewManagement,
}) {
  const dispatch = useDispatch();
  const [organization, setOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !organizationName) {
      setOrganization(null);
      return;
    }

    const fetchOrganization = async () => {
      setIsLoading(true);
      try {
        const organizations = await dispatch(getAllOrganizations(1, organizationName, 1, {}, true));
        setOrganization(organizations?.[0] || null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organizationName]);

  const handleBrandSetupToggle = async (org) => {
    const currentStatus = org.is_brand_setup_complete || false;

    setOrganization((prev) => (
      prev ? { ...prev, is_brand_setup_complete: !currentStatus } : prev
    ));

    const result = await dispatch(toggleOrganizationBrandSetupFlow(org.id, currentStatus));

    if (result?.statusCode !== 200) {
      setOrganization((prev) => (
        prev ? { ...prev, is_brand_setup_complete: currentStatus } : prev
      ));
    }
  };

  const columns = [
    {
      name: 'Id',
      selector: (row) => row.id,
      sortable: false,
      center: false,
      width: '100px',
    },
    {
      name: 'Organization Name',
      cell: (row) => {
        const tooltipId = `invite-orgname-tooltip-${row.id}`;
        return (
          <>
            <div
              id={tooltipId}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name || '--'}
            </div>
            <UncontrolledTooltip placement="top" target={tooltipId}>
              {row.name || 'N/A'}
            </UncontrolledTooltip>
          </>
        );
      },
      sortable: false,
      center: false,
      grow: 1,
    },
    {
      name: 'Website',
      cell: (row) => {
        const tooltipId = `invite-website-tooltip-${row.id}`;
        return (
          <>
            <div
              id={tooltipId}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.website || '--'}
            </div>
            <UncontrolledTooltip placement="top" target={tooltipId}>
              {row.website || 'N/A'}
            </UncontrolledTooltip>
          </>
        );
      },
      sortable: false,
      center: false,
      grow: 1,
    },
    {
      name: 'Brand Setup Flow',
      cell: (row) => row?.type === 'brand' ? (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={row.is_brand_setup_complete || false}
            onChange={() => handleBrandSetupToggle(row)}
            style={{ cursor: 'pointer' }}
            title={row.is_brand_setup_complete ? 'Brand Setup Completed' : 'Brand Setup Pending'}
          />
          <label
            className="form-check-label ms-1 small"
            style={{
              color: row.is_brand_setup_complete ? '#2e7d32' : '#757575',
              fontSize: '11px'
            }}
          >
            {row.is_brand_setup_complete ? 'Active' : 'Inactive'}
          </label>
        </div>
      ) : <span>--</span>,
      center: true,
      width: '160px',
    },
  ];

  return (
    <Modal isOpen={open} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        Invitation Sent Successfully
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
          The organization has been created. Review the details below.
        </p>
        <div className="table-responsive">
          {isLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <DataTable
              striped
              data={organization ? [organization] : []}
              columns={columns}
              className="display"
              noDataComponent="Organization not found"
              customStyles={{
                table: { style: { marginBottom: 0 } },
              }}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          Close
        </Button>
        <Button color="primary" onClick={onViewManagement}>
          Go to Management
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default InviteSuccessModal;
